import React, { ReactNode } from 'react';
import { usePermissions } from '@/hooks/usePermissions';
import { Permission } from '@/types/model';

interface PermissionGateProps {
  /** Children to render if user has permission */
  children: ReactNode;
  
  /** Required permission to view the content */
  permission?: Permission;
  
  /** Array of permissions - user needs ANY of these */
  anyPermission?: Permission[];
  
  /** Array of permissions - user needs ALL of these */
  allPermissions?: Permission[];
  
  /** Required role to view the content */
  role?: string;
  
  /** Array of roles - user needs ANY of these */
  anyRole?: string[];
  
  /** If true, only admin/superadmin can see content */
  adminOnly?: boolean;
  
  /** Component to render if user doesn't have permission */
  fallback?: ReactNode;
  
  /** If true, render nothing instead of fallback when no permission */
  hideWhenNoPermission?: boolean;
}

/**
 * Component that conditionally renders content based on user permissions
 */
export const PermissionGate: React.FC<PermissionGateProps> = ({
  children,
  permission,
  anyPermission,
  allPermissions,
  role,
  anyRole,
  adminOnly = false,
  fallback = null,
  hideWhenNoPermission = false,
}) => {
  const { 
    hasPermission, 
    hasAnyPermission, 
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    isAdmin 
  } = usePermissions();

  // Check admin-only access
  if (adminOnly && !isAdmin()) {
    return hideWhenNoPermission ? null : <>{fallback}</>;
  }

  // Check single permission
  if (permission && !hasPermission(permission)) {
    return hideWhenNoPermission ? null : <>{fallback}</>;
  }

  // Check any permissions
  if (anyPermission && anyPermission.length > 0 && !hasAnyPermission(anyPermission)) {
    return hideWhenNoPermission ? null : <>{fallback}</>;
  }

  // Check all permissions
  if (allPermissions && allPermissions.length > 0 && !hasAllPermissions(allPermissions)) {
    return hideWhenNoPermission ? null : <>{fallback}</>;
  }

  // Check single role
  if (role && !hasRole(role)) {
    return hideWhenNoPermission ? null : <>{fallback}</>;
  }

  // Check any roles
  if (anyRole && anyRole.length > 0 && !hasAnyRole(anyRole)) {
    return hideWhenNoPermission ? null : <>{fallback}</>;
  }

  // User has permission, render children
  return <>{children}</>;
};